import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import H4 from 'UIComponents/elements/headings/H4';
import UIFlex from 'UIComponents/layout/UIFlex';
import Small from 'UIComponents/elements/Small';
import UILink from 'UIComponents/link/UILink';
import UIPopover from 'UIComponents/tooltip/UIPopover';
import UIPopoverBody from 'UIComponents/tooltip/UIPopoverBody';
import UIPopoverCloseButton from 'UIComponents/tooltip/UIPopoverCloseButton';
import UIPopoverFooter from 'UIComponents/tooltip/UIPopoverFooter';
import UIPopoverHeader from 'UIComponents/tooltip/UIPopoverHeader';
import UIButton from 'UIComponents/button/UIButton';
import { BATTLESHIP } from 'HubStyleTokens/colors';
import CoachingTipButton from './buttons/CoachingTipButton';
import CoachingTipSteps from './CoachingTipSteps';
import useTipHandlers from '../hooks/useTipHandlers';
import { trackUserInteraction } from '../utils/usageTracker';
import { useTarget } from '../hooks/useTarget';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const StyledUIPopoverFooter = styled(UIPopoverFooter).withConfig({
  displayName: "CoachingTip__StyledUIPopoverFooter",
  componentId: "sc-1u5yqfa-0"
})([".hs-private.hs-private.hs-private &{border-top:1px solid ", ";}"], BATTLESHIP);

// Placing coaching tips under the earth layer due to the menu on mobile devices being on earth layer.
// This fixes the issue where caoching tips renders on top of the menu
// Hardcoding the layer to 1000 as z-index token is not yet supported in Trellis
export const EARTH_LAYER = '1000';
const COACHING_TIPS_LAYER = parseInt(EARTH_LAYER, 10) - 1;
const CoachingTip = ({
  actionText,
  attachTo,
  beforeEnter,
  className,
  cta,
  dismissCoachingTips,
  fallbackElementQuery,
  coachingTipsId,
  isOpen,
  onTipRender,
  onTipUnmount,
  placement,
  popoverPlacement,
  requiresTargetDisappearAnimation,
  setCurrentTipId,
  steps,
  tipId,
  tipIndex,
  title,
  zIndex = COACHING_TIPS_LAYER,
  popoverZIndex = zIndex
}) => {
  const {
    isTargetVisible,
    targetElement
  } = useTarget(attachTo, {
    requiresTargetDisappearAnimation,
    beforeEnter,
    fallbackElementQuery
  });
  const tipViewed = useRef(false);
  const {
    text: ctaText,
    href: ctaHref,
    handler: ctaHandler
  } = cta || {};
  const hasCta = ctaText && (ctaHref || ctaHandler);
  const shouldShowCtaSection = actionText || hasCta;
  const {
    onOpenChangeTip,
    onStepChange,
    onTipClick,
    onTurnOffClick
  } = useTipHandlers({
    coachingTipsId,
    tipId,
    setCurrentTipId,
    isOpen,
    dismissCoachingTips
  });
  useEffect(() => {
    if (!isTargetVisible) {
      if (isOpen) {
        setCurrentTipId('');
      }
    }
  }, [isTargetVisible, isOpen, setCurrentTipId]);
  useEffect(() => {
    if (targetElement && isTargetVisible && !tipViewed.current) {
      trackUserInteraction('coaching tip viewed', coachingTipsId, tipId);
      tipViewed.current = true;
    }
  }, [targetElement, isTargetVisible, coachingTipsId, tipId]);
  if (!targetElement || !isTargetVisible) {
    return null;
  }
  return /*#__PURE__*/_jsx(UIPopover, {
    placement: popoverPlacement || 'top',
    use: "shepherd",
    width: 390,
    open: isOpen,
    "data-coaching-tips-popover": tipId,
    "data-test-id": `${tipId}-coaching-tip-popover`,
    onOpenChange: onOpenChangeTip,
    zIndex: popoverZIndex,
    content: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(UIPopoverHeader, {
        children: [/*#__PURE__*/_jsx(H4, {
          children: title
        }), /*#__PURE__*/_jsx(UIPopoverCloseButton, {
          "data-test-id": "coaching-tip-popover-close-button",
          popoverUse: "shepherd",
          onClick: onTipClick
        })]
      }), /*#__PURE__*/_jsxs(UIPopoverBody, {
        className: "p-x-1 p-y-1",
        children: [/*#__PURE__*/_jsx(CoachingTipSteps, {
          steps: steps,
          onStepChange: onStepChange
        }), shouldShowCtaSection && /*#__PURE__*/_jsxs(UIFlex, {
          align: "center",
          justify: "center",
          className: "m-bottom-3",
          "data-test-id": "coaching-tip-cta-section",
          children: [hasCta && /*#__PURE__*/_jsx(UIButton, {
            "data-test-id": "coaching-tip-cta",
            href: ctaHref
            //
            ,
            onClick: ctaHandler !== null ? ctaHandler : undefined,
            use: "primary",
            children: ctaText
          }), !hasCta && actionText && /*#__PURE__*/_jsx("b", {
            children: actionText
          })]
        })]
      }), /*#__PURE__*/_jsx(StyledUIPopoverFooter, {
        children: /*#__PURE__*/_jsx(UIFlex, {
          align: "center",
          justify: "center",
          children: /*#__PURE__*/_jsx(UILink, {
            "data-selenium-test": "turn-off-coaching-tips",
            "data-test-id": "coaching-tip-turn-off-hints-cta",
            onClick: onTurnOffClick,
            className: "m-top-3",
            children: /*#__PURE__*/_jsx(Small, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "coachingTips.turnOffHints"
              })
            })
          })
        })
      })]
    }),
    children: /*#__PURE__*/_jsx(CoachingTipButton, {
      className: className,
      target: targetElement,
      onTipClick: onTipClick,
      onTipRender: onTipRender,
      onTipUnmount: onTipUnmount,
      placement: placement,
      tipId: tipId,
      tipIndex: tipIndex,
      zIndex: zIndex
    })
  });
};
export default CoachingTip;